import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send } from "lucide-react";
import { track } from "../../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const GREETING = {
  id: "greeting",
  role: "bot",
  text: "Hello — I'm Augustus's practice assistant. Ask me about his evaluation experience, services, methodologies, or how to commission an engagement.",
};
const SUGGESTIONS = [
  "What services does Augustus offer?",
  "Which regions has he worked in?",
  "Tell me about his evaluation methods",
];

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(`web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setMessages((m) => [...m, { id: uid(), role: "user", text: content }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: content,
      });
      setMessages((m) => [...m, { id: uid(), role: "bot", text: data.response }]);
    } catch (err) {
      setMessages((m) => [...m, {
        id: uid(),
        role: "bot",
        text: "I'm having trouble responding right now. Please email augustus.316@gmail.com and he'll get back to you.",
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div className="chat-panel" role="dialog" aria-label="Practice assistant" data-testid="chat-panel">
          <div className="chat-head">
            <div className="chat-head-avatar" aria-hidden="true">AE</div>
            <div>
              <div className="chat-head-title">Evidence Assistant</div>
              <div className="chat-head-sub">Online</div>
            </div>
            <button className="chat-close" aria-label="Close assistant" onClick={() => setOpen(false)} data-testid="chat-close">
              <X size={18} />
            </button>
          </div>

          <div className="chat-body" ref={bodyRef}>
            {messages.map((m) => (
              <div key={m.id} className={`chat-msg ${m.role}`} data-testid={`chat-msg-${m.role}`}>{m.text}</div>
            ))}
            {messages.length === 1 && (
              <div className="chat-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="chat-suggestion" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            )}
            {busy && (
              <div className="chat-msg bot">
                <span className="chat-typing"><span /><span /><span /></span>
              </div>
            )}
          </div>

          <form className="chat-input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input className="chat-input" placeholder="Ask a question…" value={input}
                   onChange={(e) => setInput(e.target.value)} data-testid="chat-input" aria-label="Message" />
            <button type="submit" className="chat-send" disabled={busy || !input.trim()} data-testid="chat-send" aria-label="Send">
              <Send size={18} />
            </button>
          </form>
        </div>
      )}

      <button className="chat-fab" onClick={() => setOpen((o) => { const next = !o; if (next) track("chat_open"); return next; })}
              aria-label={open ? "Close assistant" : "Open assistant"} data-testid="chat-fab">
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>
    </>
  );
};
